import { useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { UploadCloud, Loader2, RotateCcw, Sparkles, Mail, Check, ArrowRight } from "lucide-react";
import { FadeUp, scrollToId } from "@/components/motion";
import { track } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEEDBACK_LANGUAGES = [
  "English", "Arabic", "Chinese (Simplified)", "Chinese (Traditional)", "Vietnamese",
  "Hindi", "Punjabi", "Spanish", "Filipino (Tagalog)", "Korean", "Greek", "Italian",
  "Nepali", "Urdu", "Thai", "Indonesian", "Japanese", "French", "Persian (Farsi)", "Tamil",
];

function TranslatedSummary({ ts }) {
  if (!ts || !ts.language) return null;
  return (
    <div data-testid="translated-summary" className="mt-8 border border-[#27272A] border-l-2 border-l-[#DFFF00] bg-[#0A0A0A] p-6 max-w-md">
      <p data-testid="translated-heading" className="font-display text-lg font-bold text-[#F8F8F5]">
        {ts.heading || `Your summary in ${ts.language}`}
      </p>
      <p className="mt-3 font-body text-sm text-[#D4D4D8] leading-relaxed">{ts.intro}</p>
      {Array.isArray(ts.strengths) && ts.strengths.length > 0 && (
        <ul className="mt-4 space-y-2 pl-5 list-disc marker:text-[#DFFF00]">
          {ts.strengths.map((s, i) => (
            <li key={i} className="font-body text-sm text-[#A1A1AA] leading-relaxed">{s}</li>
          ))}
        </ul>
      )}
      {Array.isArray(ts.improvements) && ts.improvements.length > 0 && (
        <ol className="mt-4 space-y-2 pl-5 list-decimal marker:text-[#DFFF00]">
          {ts.improvements.map((s, i) => (
            <li key={i} className="font-body text-sm text-[#A1A1AA] leading-relaxed">{s}</li>
          ))}
        </ol>
      )}
      {ts.next_steps && <p className="mt-4 font-body text-sm text-[#D4D4D8] leading-relaxed">{ts.next_steps}</p>}
    </div>
  );
}

// Maps each scorecard category to plain-language guidance on how paid support helps.
const CATEGORY_SERVICE_MAP = {
  "Career positioning": {
    service: "Career Strategy, CV and LinkedIn",
    why: "Your pitch needs to be clear everywhere an employer looks. We help you build it, then shape your CV and LinkedIn around it.",
  },
  "Achievements and evidence": {
    service: "Career Strategy, CV and LinkedIn",
    why: "We help you turn your duties into clear achievements with evidence. This is the strongest single fix for a CV that undersells real experience.",
  },
  "Clarity and readability": {
    service: "Career Strategy, CV and LinkedIn",
    why: "We rewrite your CV in clear, simple wording so your value is obvious in a recruiter's first skim.",
  },
  "ATS keywords and relevance": {
    service: "Career Strategy, CV and LinkedIn",
    why: "We align your words with the roles you want, so your CV appears in recruiter searches and passes screening software.",
  },
  "Formatting and structure": {
    service: "Career Strategy, CV and LinkedIn",
    why: "We rebuild your CV with a clear, familiar structure that recruiters and recruitment software can read easily.",
  },
};

function WeakestArea({ categories }) {
  if (!Array.isArray(categories) || categories.length === 0) return null;
  const weakest = categories.reduce((min, c) => (c.score < min.score ? c : min), categories[0]);
  const rec = CATEGORY_SERVICE_MAP[weakest.name];
  if (!rec) return null;
  return (
    <div data-testid="score-followthrough" className="mt-8 border border-[#27272A] bg-[#0A0A0A] p-6 max-w-md">
      <p className="tracking-label text-[10px] uppercase font-body font-bold text-[#52525B] mb-2">Your weakest area</p>
      <div className="flex items-baseline justify-between gap-4">
        <p data-testid="weakest-category" className="font-display text-lg font-bold tracking-tight text-[#F8F8F5]">
          {weakest.name}
        </p>
        <span className="font-display text-lg font-bold text-[#DFFF00] tabular-nums shrink-0">{weakest.score}/100</span>
      </div>
      <div className="h-1 bg-[#27272A] mt-2">
        <div className="h-full bg-[#DFFF00]" style={{ width: `${weakest.score}%` }} />
      </div>
      <p className="mt-4 font-body text-sm text-[#A1A1AA] leading-relaxed">{rec.why}</p>
      <Link
        to="/services"
        data-testid="weakest-service-link"
        onClick={() => track("weakest_service_clicked", { category: weakest.name })}
        className="mt-4 inline-flex items-center gap-1.5 font-body text-sm font-semibold text-[#DFFF00] hover:underline underline-offset-4"
      >
        How we can help: {rec.service} <ArrowRight size={14} />
      </Link>
    </div>
  );
}

function ScoreRing({ score }) {
  const r = 78;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative w-[200px] h-[200px]">
      <svg viewBox="0 0 200 200" className="w-full h-full -rotate-90" aria-hidden="true">
        <circle cx="100" cy="100" r={r} fill="none" stroke="#27272A" strokeWidth="8" />
        <motion.circle
          cx="100" cy="100" r={r} fill="none" stroke="#DFFF00" strokeWidth="8" strokeLinecap="round"
          strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: c - (c * score) / 100 }}
          transition={{ duration: 1.4, ease: [0.76, 0, 0.24, 1] }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-6xl font-bold text-[#F8F8F5]">{score}</span>
        <span className="tracking-label text-[10px] uppercase font-body font-bold text-[#52525B]">/ 100</span>
      </div>
    </div>
  );
}

export default function CVReview() {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [fileName, setFileName] = useState("");
  const [result, setResult] = useState(null);
  const [language, setLanguage] = useState("English");
  // email capture
  const [form, setForm] = useState({ first_name: "", email: "", consent: false });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const analyze = async (formData, viaUpload) => {
    setLoading(true);
    setResult(null);
    track("cv_score_started");
    if (viaUpload) track("cv_uploaded");
    try {
      const res = await axios.post(`${API}/cv/analyze`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      track("cv_score_completed", { score_band: res.data.band });
      setTimeout(() => scrollToId("#cv-result"), 250);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assessment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("feedback_language", language);
    analyze(fd, true);
  };

  const handleText = () => {
    if (text.trim().length < 80) {
      toast.error("Please paste at least a few lines of your CV.");
      return;
    }
    const fd = new FormData();
    fd.append("text", text);
    fd.append("feedback_language", language);
    analyze(fd, false);
  };

  const requestReport = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email) {
      toast.error("Please add your first name and email.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/score/report`, { analysis_id: result.id, ...form });
      setSent(true);
      track("detailed_report_requested", { consent: form.consent });
      toast.success("Your detailed report is on its way.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setResult(null);
    setText("");
    setFileName("");
    setForm({ first_name: "", email: "", consent: false });
    setSent(false);
  };

  return (
    <section data-testid="cv-review" id="cv-review" className="relative bg-[#141414] py-28 md:py-40 border-y border-[#27272A]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <FadeUp className="mb-14 max-w-2xl">
          <p className="tracking-label text-[11px] uppercase font-body font-bold text-[#DFFF00] mb-6">Free CV Career Brand Score</p>
          <h2 className="font-display font-bold tracking-tighter leading-[0.95] text-4xl md:text-6xl text-[#F8F8F5]">
            Score your CV in <span className="font-serif-accent font-light">seconds.</span>
          </h2>
          <p className="mt-6 font-body text-[#A1A1AA] leading-relaxed">
            Upload or paste your CV for a clear, practical assessment of how it performs across the areas
            recruiters and applicant tracking systems (ATS) consider — career positioning, achievements and
            evidence, clarity and readability, ATS keywords and relevance, and formatting and structure.
            No signup needed to see your score.
          </p>
        </FadeUp>

        <AnimatePresence mode="wait">
          {!result && (
            <motion.div key="uploader" exit={{ opacity: 0, y: -20 }} className="grid md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <p data-testid="cv-translate-note" className="font-body text-sm text-[#A1A1AA] leading-relaxed">
                  You can use your browser's Translate option to read this website in another language.
                  To receive a translated CV feedback summary, choose your language below.
                </p>
                <p data-testid="cv-language-pitch" className="mt-2 font-body text-sm text-[#A1A1AA] leading-relaxed">
                  <span className="text-[#F8F8F5] font-semibold">Prefer feedback in another language?</span>{" "}
                  Choose from 20 languages when submitting your CV. You will receive your full scorecard in
                  plain English, plus a summary in your chosen language.
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <label htmlFor="cv-language" className="tracking-label text-[10px] uppercase font-body font-bold text-[#52525B]">
                    Feedback language (optional)
                  </label>
                  <select
                    id="cv-language"
                    data-testid="cv-language-select"
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    className="bg-[#0A0A0A] border border-[#27272A] font-body text-sm text-[#F8F8F5] px-4 py-2.5 focus:border-[#DFFF00] outline-none transition-colors cursor-pointer"
                  >
                    {FEEDBACK_LANGUAGES.map((l) => (
                      <option key={l} value={l}>{l === "English" ? "English (default)" : l}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div
                data-testid="cv-dropzone"
                role="button"
                tabIndex={0}
                aria-label="Upload your CV file"
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
                onClick={() => inputRef.current?.click()}
                className={`cursor-pointer min-h-[320px] flex flex-col items-center justify-center text-center p-10 transition-all duration-300 ${
                  dragging ? "border border-[#DFFF00] bg-[#DFFF00]/5" : "border border-dashed border-white/20 hover:border-white/40"
                }`}
              >
                <input
                  ref={inputRef}
                  data-testid="cv-file-input"
                  type="file"
                  accept=".pdf,.docx,.txt"
                  aria-label="Choose a CV file to upload"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
                {loading && fileName ? (
                  <Loader2 className="animate-spin text-[#DFFF00]" size={40} />
                ) : (
                  <UploadCloud className={dragging ? "text-[#DFFF00]" : "text-[#52525B]"} size={40} />
                )}
                <p className="mt-5 font-body font-semibold text-[#F8F8F5]">
                  {fileName || "Drag & drop your CV here"}
                </p>
                <p className="mt-2 font-body text-sm text-[#52525B]">or click to browse · PDF, DOCX, TXT</p>
              </div>

              <div className="flex flex-col">
                <label htmlFor="cv-text" className="sr-only">Paste your CV text</label>
                <textarea
                  id="cv-text"
                  data-testid="cv-text-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="…or paste your CV text here"
                  className="brutalist-input flex-1 min-h-[240px] resize-none border border-[#27272A] p-5 leading-relaxed"
                  style={{ borderBottomColor: "#27272A" }}
                />
                <button
                  data-testid="cv-analyze-text-btn"
                  onClick={handleText}
                  disabled={loading}
                  className="mt-4 rounded-full bg-[#DFFF00] text-[#0A0A0A] font-body font-semibold px-7 py-4 flex items-center justify-center gap-2 hover:bg-[#C8E600] transition-colors duration-300 disabled:opacity-60"
                >
                  {loading && !fileName ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
                  {loading && !fileName ? "Assessing…" : "Get your free CV Career Brand Score"}
                </button>
              </div>
              <p data-testid="cv-retention-note" className="md:col-span-2 font-body text-xs text-[#52525B] leading-relaxed">
                Your uploaded document is securely retained and accessible only to George for your assessment.{" "}
                <Link to="/data-handling" className="text-[#A1A1AA] underline underline-offset-4 hover:text-[#DFFF00] transition-colors">
                  See CV Data Handling
                </Link>{" "}
                for details or to request deletion.
              </p>
            </motion.div>
          )}

          {result && (
            <motion.div
              key="result" id="cv-result" data-testid="cv-result"
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            >
              <div className="grid lg:grid-cols-12 gap-10 items-start">
                {/* Teaser: score + band + observation + action */}
                <div className="lg:col-span-6 flex flex-col items-center lg:items-start">
                  <ScoreRing score={result.overall_score} />
                  <p data-testid="cv-band" className="mt-6 tracking-label text-[11px] uppercase font-body font-bold text-[#DFFF00]">
                    {result.band}
                  </p>
                  <p data-testid="cv-observation" className="mt-3 font-body text-lg text-[#F8F8F5] leading-relaxed">
                    {result.headline_observation || result.verdict}
                  </p>
                  <div className="mt-6 border-l-2 border-[#DFFF00] pl-4">
                    <p className="tracking-label text-[10px] uppercase font-body font-bold text-[#52525B] mb-1">Your #1 next step</p>
                    <p data-testid="cv-action" className="font-body text-[#A1A1AA] leading-relaxed">{result.top_action}</p>
                  </div>
                  <WeakestArea categories={result.categories} />
                  <TranslatedSummary ts={result.translated_summary} />
                  <button
                    data-testid="cv-reset-btn" onClick={reset}
                    className="mt-8 flex items-center gap-2 text-sm font-body text-[#A1A1AA] hover:text-[#DFFF00] transition-colors"
                  >
                    <RotateCcw size={15} /> Score another CV
                  </button>
                </div>

                {/* Email capture for the full report */}
                <div className="lg:col-span-6">
                  <AnimatePresence mode="wait">
                    {!sent ? (
                      <motion.form
                        key="capture" data-testid="report-form" onSubmit={requestReport} exit={{ opacity: 0 }}
                        className="bg-[#0A0A0A] border border-[#27272A] p-8"
                      >
                        <p className="tracking-label text-[10px] uppercase font-body font-bold text-[#DFFF00] mb-3 flex items-center gap-2">
                          <Mail size={13} /> Your full report + action plan
                        </p>
                        <h3 className="font-display text-2xl font-bold text-[#F8F8F5] leading-tight">
                          Where should we send your complete Career Brand Score and personalised action plan?
                        </h3>
                        <div className="mt-6 space-y-5">
                          <div>
                            <label htmlFor="rep-first" className="tracking-label text-[10px] uppercase font-body font-bold text-[#52525B]">First name</label>
                            <input id="rep-first" data-testid="report-first-name" className="brutalist-input"
                              value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} />
                          </div>
                          <div>
                            <label htmlFor="rep-email" className="tracking-label text-[10px] uppercase font-body font-bold text-[#52525B]">Email address</label>
                            <input id="rep-email" type="email" data-testid="report-email" className="brutalist-input"
                              value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                          </div>
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input type="checkbox" data-testid="report-consent" className="mt-1 accent-[#DFFF00] w-4 h-4"
                              checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} />
                            <span className="font-body text-xs text-[#A1A1AA] leading-relaxed">
                              I agree to receive my Career Brand Score and occasional career-branding advice from
                              Barrett Career Brand. I can unsubscribe at any time.
                            </span>
                          </label>
                        </div>
                        <button
                          type="submit" data-testid="report-submit" disabled={sending}
                          className="mt-6 w-full rounded-full bg-[#DFFF00] text-[#0A0A0A] font-body font-semibold px-7 py-4 flex items-center justify-center gap-2 hover:bg-[#C8E600] transition-colors duration-300 disabled:opacity-60"
                        >
                          {sending ? <Loader2 className="animate-spin" size={18} /> : null}
                          {sending ? "Sending…" : "Email me my full report"}
                        </button>
                        <p className="mt-3 font-body text-[11px] text-[#52525B]">
                          We only use your details to send your report. See our Privacy Policy.
                        </p>
                      </motion.form>
                    ) : (
                      <motion.div
                        key="sent" data-testid="report-success" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
                        className="bg-[#0A0A0A] border border-[#27272A] p-8"
                      >
                        <div className="w-14 h-14 rounded-full bg-[#DFFF00] flex items-center justify-center">
                          <Check className="text-[#0A0A0A]" size={26} />
                        </div>
                        <h3 className="font-display text-2xl font-bold text-[#F8F8F5] mt-6 leading-tight">
                          Your detailed Career Brand Score is on its way.
                        </h3>
                        <p className="font-body text-[#A1A1AA] mt-3 leading-relaxed">
                          Would you also like to discuss your results with George?
                        </p>
                        <button
                          data-testid="report-book-btn" onClick={() => { track("consultation_started", { from: "score" }); scrollToId("#consultation"); }}
                          className="mt-6 rounded-full bg-[#DFFF00] text-[#0A0A0A] font-body font-semibold px-7 py-4 inline-flex items-center gap-2 hover:bg-[#C8E600] transition-colors duration-300"
                        >
                          Request a free 15-minute consultation <ArrowRight size={17} />
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
